<template>
  <b-modal
    :title="$t('DefineDateTime')"
    v-model="localIsOpen"
    id="date-time-selection-modal"
    size="md"
    body-class="date-time-selection-modal-body"
    header-close-content=""
    centered
    @hide="onCancel"
    @ok="onOk">
    <div class="date-time-selection-modal-content" data-cy="date-time-selection-modal">
      <div>
        <strong>{{ $t("DateTimeVariationQuestion") }}</strong>
        <b-form-group v-slot="{ ariaDescribedby }" class="mb-0">
          <b-form-radio-group
            id="btn-radios-1"
            v-model="vm.isRelative"
            :options="nav"
            :aria-describedby="ariaDescribedby"
            name="radios-btn-default"
            class="radio-buttons"
            buttons
            data-cy="date-time-selection-types"
            button-variant="primary"></b-form-radio-group>
        </b-form-group>
      </div>
      <div class="date-time-group" v-show="vm.isRelative === false">
        <label>{{ $t("FixedTime") }}</label>
        <MDateAndTimePicker v-model="vm.selectedDateTime" :validation="validationFeedback" />
      </div>
      <div class="date-time-group" v-show="vm.isRelative === true">
        <label>{{ $t("RelativeTime") }}</label>
        <div class="d-flex">
          <b-dropdown class="dropdown-default w-100" toggle-class="w-100" data-cy="relative-date-ddn">
            <template #button-content>
              <span>{{ translate(vm.selectedRelativeOption) }}</span>
            </template>
            <template v-for="group in groupedOptions" :key="group.groupId">
              <b-dropdown-item
                v-for="option in group.options"
                :key="option.key"
                @click="selectOption(option)"
                :active="option.key === vm.selectedRelativeOption?.key">
                {{ translate(option) }}
              </b-dropdown-item>
              <b-dropdown-divider v-if="group.groupId !== lastGroupId" />
            </template>
          </b-dropdown>
          <b-input
            type="number"
            min="0"
            max="999"
            step="1"
            v-model="vm.selectedRelativeOptionValue"
            v-show="vm.selectedRelativeOption?.type === 'singleVar'"
            inputmode="numeric"
            autocomplete="off"
            v-tooltip="validationFeedback"
            class="relative-value"
            :class="{ 'border-danger': validationFeedback }" />
        </div>
        <div>
          <b-alert class="m-alert mt-1 p-0" show variant="light">
            <div class="m-alert-icon">
              <Icon icon="Info" size="12px"></Icon>
            </div>
            <div class="m-alert-content">
              {{ $t("RelativeDateInfoText") }}
            </div>
          </b-alert>
        </div>
      </div>
      <div class="date-time-group" v-show="vm.isRelative === null">
        <Alert show with-icon>
          {{ $t("EmptyValueSelected") }}
        </Alert>
      </div>
    </div>
    <template #modal-footer="{ ok, cancel }">
      <div class="footer">
        <div class="footer-result">
          <span class="absolute">{{ selectionPreview }}</span>
        </div>
        <div class="footer-actions">
          <b-btn @click="ok" variant="primary" :disabled="validationFeedback.length > 0" data-cy="date-time-selection-confirm-btn">{{ $t("OK") }}</b-btn>
          <b-btn @click="cancel" data-cy="date-time-selection-cancel-btn">{{ $t("Cancel") }}</b-btn>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import MDateAndTimePicker from "@/BasicComponentLibrary/MDateAndTimePicker.vue";
import _ from "lodash";
import Icon from "@common/components/Icon.vue";
import { computed, reactive, withDefaults, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getRelativeDate, RelativeTimeOption, relativeTimeOptions } from "@util/relativeDate";
import Alert from "@common/components/Alert.vue";

//#region General
const props = withDefaults(
  defineProps<{ isOpen: boolean; isRelative: boolean | null; canBeNone: boolean; time: string; optionKey: string; optionValue: number }>(),
  {
    isRelative: false,
    canBeNone: false,
    time: "",
    optionKey: "today",
    optionValue: 1
  }
);
const emit = defineEmits(["update:isOpen", "confirm", "cancelSelection"]);

const { t: $t, d: $d } = useI18n();
//#endregion

//#region ViewModel
type ViewModel = {
  isRelative: boolean | null;
  selectedDateTime: string;
  selectedRelativeOption: RelativeTimeOption | undefined;
  selectedRelativeOptionValue: number;
};
const vm = reactive<ViewModel>({
  isRelative: props.isRelative,
  selectedDateTime: props.time,
  selectedRelativeOption: relativeTimeOptions.find((o) => o.key === props.optionKey),
  selectedRelativeOptionValue: props.optionValue
});

const nav = computed(() => {
  const initialOptions = [
    {
      text: $t("Fixed"),
      value: false
    },
    {
      text: $t("Relative"),
      value: true
    }
  ];

  if (!props.canBeNone) {
    return initialOptions;
  }

  const noneOption = {
    text: $t("None"),
    value: null
  };

  return [...initialOptions, noneOption];
});
//#endregion

//#region Localisations
function translate(option: RelativeTimeOption | undefined): string {
  if (!option || option.key === "") return "";
  const localKey = "relativeDateTimePicker.";
  return $t(localKey + option.key);
}
//#endregion

//#region Relative Time
const groupedOptions = computed(() => {
  const groups = new Map<number, RelativeTimeOption[]>();

  for (const option of relativeTimeOptions) {
    if (!groups.has(option.groupId)) {
      groups.set(option.groupId, []);
    }
    groups.get(option.groupId)!.push(option);
  }

  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([groupId, options]) => ({
      groupId,
      options: options.sort((a, b) => a.sequence - b.sequence)
    }));
});

const lastGroupId = computed(() => Math.max(...groupedOptions.value.map((group) => group.groupId)));

const selectOption = (option: RelativeTimeOption) => {
  vm.selectedRelativeOption = option;
  if (vm.selectedRelativeOption.type !== "singleVar") vm.selectedRelativeOptionValue = 1;
};
//#endregion

//#region Result
const selectionPreview = computed(() => {
  if (vm.isRelative === null) return `<${$t("empty")}>`;
  else if (vm.isRelative) {
    if (validationFeedback.value) return "-";
    const result = getRelativeDate({ key: vm.selectedRelativeOption?.key, value: vm.selectedRelativeOptionValue });
    return result.display;
  } else {
    if (_.isEmpty(vm.selectedDateTime)) return $t("SpecifyTime");
    const containsOnlyDate = vm.selectedDateTime.split(" ").length === 1 ? true : false;
    return getDay() + ", " + $d(vm.selectedDateTime, containsOnlyDate ? "shortDate" : "shortDateTime");
  }
});

function getDay(): string {
  const day = new Date(vm.selectedDateTime).getDay();
  const dayName = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][day];
  return $t("datepicker." + dayName);
}
//#endregion

//#region Updatable Props
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      vm.isRelative = props.isRelative;
      vm.selectedDateTime = props.time;
      vm.selectedRelativeOption = relativeTimeOptions.find((o) => o.key === props.optionKey);
      vm.selectedRelativeOptionValue = props.optionValue;
    }
  }
);

const localIsOpen = computed({
  get() {
    return props.isOpen;
  },
  set(nv) {
    emit("update:isOpen", nv);
  }
});
//#endregion

const onOk = () => {
  let value: string | undefined = "";

  if (vm.isRelative !== null) {
    if (!vm.isRelative) value = vm.selectedDateTime;
    else {
      value =
        vm.selectedRelativeOptionValue > 1
          ? vm.selectedRelativeOption?.backendKey?.plural?.replace("{value}", vm.selectedRelativeOptionValue.toString())
          : vm.selectedRelativeOption?.backendKey?.singular;
    }
  }
  emit("confirm", value ?? "");
};
const onCancel = (event: Event & { trigger: string }) => {
  if (event.trigger !== "ok") {
    emit("cancelSelection");
  }
};
//#endregion

//#region Validation
const validationFeedback: Record<string, any> = computed(() => {
  if (vm.isRelative === false) {
    return vm.selectedDateTime === "" ? $t("isMandatoryValue") : "";
  } else if (vm.isRelative === true) {
    if (vm.selectedRelativeOptionValue < 1) return $t("isMandatoryValue");
    const result = Number(vm.selectedRelativeOptionValue);
    return vm.isRelative === true && typeof result === "number" && Number.isFinite(result) ? "" : $t("ValueIsNotNumber");
  }
  return "";
});
//#endregion
</script>
